import React, { useMemo, useState } from 'react';
import ReRegistrationSuccessEvidenceModal from '../Feed/ReRegistrationSuccessEvidenceModal';
import {
    fetchSuperAdminReRegistrationSuccessMembers,
    isSuperAdminReRegistrationEvidenceUnavailable,
} from '../../services/useSuperAdminDashboardQueryService';
import type { ReRegistrationSuccessCenterBreakdown } from './centerReRegistrationSuccess';

type DataQuality = 'EXACT' | 'ESTIMATED' | 'MIXED';

/**
 * 근거 모달 대상.
 * - 지점: 그 지점 전체(coachId 없음). 숫자는 지점 행 값 그대로.
 * - 강사: 서버가 준 지점 분해(centers[]) 순서대로 탭. 지점이 1곳이면 탭 없이 그 지점.
 *   숫자는 **선택한 지점의 분해값**(합산값이 아니다 — 목록이 그 지점 것이므로).
 */
export type SuperAdminReRegistrationEvidenceTarget =
    | {
          kind: 'center';
          centerId: string;
          centerName: string;
          cohortMonth: string;
          rate: number | null;
          succeededMemberCount?: number;
          endedMemberCount?: number;
          notYetMemberCount?: number;
          dataQuality?: DataQuality;
      }
    | {
          kind: 'coach';
          coachId: string;
          coachName: string;
          cohortMonth: string;
          centers: ReRegistrationSuccessCenterBreakdown[];
          dataQuality?: DataQuality;
      };

type Props = {
    superAdminId: string;
    target: SuperAdminReRegistrationEvidenceTarget;
    onClose: () => void;
};

const SuperAdminReRegistrationEvidenceModal = ({ superAdminId, target, onClose }: Props) => {
    const fetchMembers = useMemo(() => fetchSuperAdminReRegistrationSuccessMembers(superAdminId), [superAdminId]);
    const coachCenters = target.kind === 'coach' ? target.centers : [];
    const [selectedCenterId, setSelectedCenterId] = useState<string>(coachCenters[0]?.centerId ?? '');

    if (target.kind === 'center') {
        return (
            <ReRegistrationSuccessEvidenceModal
                centerId={target.centerId}
                cohortMonth={target.cohortMonth}
                successRate={target.rate}
                succeededMemberCount={target.succeededMemberCount}
                endedMemberCount={target.endedMemberCount}
                notYetMemberCount={target.notYetMemberCount}
                dataQuality={target.dataQuality}
                onClose={onClose}
                fetchMembers={fetchMembers}
                showSearch={false}
                isUnavailableError={isSuperAdminReRegistrationEvidenceUnavailable}
                headerSlot={<ScopeLine text={target.centerName} />}
            />
        );
    }

    const selected = coachCenters.find((c) => c.centerId === selectedCenterId) ?? coachCenters[0];
    if (!selected) return null;
    const isMultiCenter = coachCenters.length > 1;
    // 합산 품질이 MIXED 면 지점별 품질을 알 수 없다 → 여러 지점일 때는 적지 않는다(행마다 품질이 따로 찍힌다)
    const dataQuality = isMultiCenter && target.dataQuality === 'MIXED' ? undefined : target.dataQuality;

    return (
        <ReRegistrationSuccessEvidenceModal
            centerId={selected.centerId}
            coachId={target.coachId}
            cohortMonth={target.cohortMonth}
            successRate={selected.rate}
            succeededMemberCount={selected.succeededMemberCount}
            endedMemberCount={selected.endedMemberCount}
            notYetMemberCount={selected.notYetMemberCount}
            dataQuality={dataQuality}
            onClose={onClose}
            fetchMembers={fetchMembers}
            showSearch={false}
            isUnavailableError={isSuperAdminReRegistrationEvidenceUnavailable}
            headerSlot={
                <>
                    <ScopeLine text={isMultiCenter ? `${target.coachName} 강사` : `${target.coachName} 강사 · ${selected.centerName}`} />
                    {isMultiCenter && (
                        <div className="mt-[14px] flex flex-wrap gap-[8px]" role="tablist" aria-label="지점 선택">
                            {coachCenters.map((c) => {
                                const isActive = c.centerId === selected.centerId;
                                return (
                                    <button
                                        key={c.centerId}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        onClick={() => setSelectedCenterId(c.centerId)}
                                        className={`rounded-[8px] border px-[12px] py-[6px] text-[13px] font-medium transition-colors ${
                                            isActive
                                                ? 'border-[#8DCDBA] bg-[#EAF8F3] text-[#137A5D]'
                                                : 'border-[#E4E7EC] bg-white text-[#69717D] hover:bg-[#F7F8FA]'
                                        }`}
                                    >
                                        {c.centerName}
                                        <span className={`ml-[6px] text-[12px] ${isActive ? 'text-[#169873]' : 'text-[#98A0AA]'}`}>
                                            {`${c.endedMemberCount}명 중 ${c.succeededMemberCount}명`}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            }
        />
    );
};

const ScopeLine = ({ text }: { text: string }) => (
    <p className="mt-[10px] text-[13px] font-medium leading-[18px] text-[#3E464F]">{text}</p>
);

export default SuperAdminReRegistrationEvidenceModal;
